import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { LEGAL_DOCS } from '@/data/legalDocs';
import { X } from 'lucide-react';

// Stable animation configs (defined once, not recreated on every render)
const OVERLAY_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL_MOTION = {
  initial: { opacity: 0, y: 24, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 24, scale: 0.98 },
  transition: { duration: 0.28, ease: 'easeOut' },
};


export const LegalModal = ({ docKey, onClose }) => {
  const { t, lang } = useLang();
  const doc = docKey ? (LEGAL_DOCS[lang] || LEGAL_DOCS.es)[docKey] : null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (doc) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [doc, onClose]);

  return (
    <AnimatePresence>
      {doc && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-stretch sm:items-center justify-center p-0 sm:p-6"
          {...OVERLAY_FADE}
          data-testid="legal-modal"
        >
          <div className="absolute inset-0 bg-[var(--cea-overlay)] backdrop-blur-sm" onClick={onClose} />
          <motion.div
            {...PANEL_MOTION}
            className="relative z-10 w-full sm:max-w-2xl bg-[#0b241b] sm:rounded-2xl border border-[var(--cea-border-strong)] overflow-hidden flex flex-col max-h-screen sm:max-h-[88vh]"
          >
            <div className="flex items-start justify-between gap-4 p-6 sm:p-8 border-b border-white/10 flex-shrink-0">
              <div>
                <h2 className="font-display font-medium uppercase text-2xl sm:text-3xl tracking-wide text-white">{doc.title}</h2>
                {doc.updated && <p className="mt-1 text-xs text-white/45">{doc.updated}</p>}
              </div>
              <button
                data-testid="legal-modal-close"
                onClick={onClose}
                className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[#040f0c]/80 border border-[var(--cea-border)] text-white hover:text-[var(--cea-accent)] transition-colors duration-200"
                aria-label={t.modal.close}
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 sm:p-8 space-y-5">
              {doc.sections.map((s) => (
                <div key={s.h}>
                  <h3 className="font-semibold text-[var(--cea-accent)] text-sm uppercase tracking-wide">{s.h}</h3>
                  {s.p && <p className="mt-1.5 text-sm text-white/75 leading-relaxed">{s.p}</p>}
                  {s.items && (
                    <ul className="mt-2 space-y-1.5 pl-4">
                      {s.items.map((it, j) => (
                        <li key={`${s.h}-${j}`} className="relative text-sm text-white/75 leading-relaxed before:absolute before:-left-4 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[var(--cea-accent)]">
                          {it}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
